import { Iso6393Language, TranslatedLanguage } from '@src/types/Language';
import { LangResult } from '@src/types/Filters';
import { iso6393languages } from '@src/assets/iso6393';

interface Iso6393languageDetail {
  name: string
  iso6393: string
}

interface Language {
  index: string
  code: Iso6393Language
  name: TranslatedLanguage
}

const DEFAULT_SEARCH_LIMIT = 25;

// Map languages to be easily findable
const ISO6393_LANGUAGES: { [key: string]: Iso6393languageDetail } = {};
iso6393languages.forEach((l) => ISO6393_LANGUAGES[l.i] = {
  name: l.n,
  iso6393: l.i,
});

/**
 * Help searching for a language in the given
 * language set
 */
export class LanguageSearch {
  private languages: Language[] = [];

  public constructor(
    codes: Iso6393Language[],
  ) {
    // Create list in which to search
    this.languages = codes.map((code) => {
      const l = ISO6393_LANGUAGES[code];
      if (!l) {
        console.warn(`Language ${code} not found in the base`);
        return null;
      }
      return {
        index: this.uniformString(l.name),
        name: l.name,
        code,
      };
    }).filter((l): l is Language => !!l);
  }

  public static translateIso6393(code: Iso6393Language): TranslatedLanguage {
    return ISO6393_LANGUAGES[code]?.name || 'Unknown';
  }

  /**
     * Search a language and return possible translated values with iso codes
     * If no input is provided, the first entries are returned
     */
  public searchLang(
    value: string,
    limit: number = DEFAULT_SEARCH_LIMIT,
  ): LangResult[] {
    const res: (LangResult & { i: number })[] = [];

    if (!value) {
      return this.languages.slice(0, DEFAULT_SEARCH_LIMIT).map(
        ({ name, code }) => ({ lang: name, code }),
      );
    }

    const valueToSearch = this.uniformString(value);
    for (const { index, name, code } of this.languages) {
      const i = index.indexOf(valueToSearch);
      if (i > -1) {
        res.push({ lang: name, code, i });
      }
    }

    return res.sort((a, b) => a.i - b.i)
      .map(({ lang, code }) => ({ lang, code }))
      .slice(0, limit);
  }

  /**
     * Get a uniform string to search
     */
  private uniformString(str: string): string {
    if (typeof str.normalize === undefined) {
      return str.toLowerCase();
    }
    // We use normalize the string to decompose characters before filtering the bad parts
    // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
    return str.normalize('NFKD')
      .replace(/[^\w\s.-]/g, '')
      .toLowerCase();
  }
}
